<template>
  <div class="alarms-header__wrapper">
    <div class="alarms-header__content">
      <h1 class="alarms-header__title">
        {{ $t('page-name.alarms-and-warnings') }}
      </h1>
      <auto-refresh-countdown-label
        class="alarms-header__countdown"
        @on-interval="onInterval"/>
      <s-sidebar
        class="alarms-header__sidebar"
        title-label="dashboard.filter"
        ref="sidebar"
        @on-show="onShowHandler"
      >
        <template #body>
          <filters
            ref="filterRef"
            :filter="filter"
            :isSidebarVisible="isSidebarVisible()"
            @search="onSearchHandler"
          />
        </template>
        <template #footer>
          <b-button
            variant="primary"
            @click="onSearchButtonClickHandler"
          >
            {{ $t('general.search') }}
          </b-button>
        </template>
      </s-sidebar>
    </div>
    <div
      v-if="items.length"
      class="alarms-header__badges"
    >
      <default-data-header
        :items="items"
        :modal-title="$t('dashboard.system-information')"
      />
    </div>
  </div>
</template>

<script>
import AutoRefreshCountdownLabel from '@/components/AutoRefreshCountdownLabel';
import Filters from '@/domain/components/generalPanel/alarmsAndWarnings/Filters';
import DefaultDataHeader from '@/components/header/DefaultDataHeader';
import periods from '@/domain/enums/periods-dropdown-filter';

export default {
  name: 'AlarmHeader',
  components: {
    AutoRefreshCountdownLabel,
    Filters,
    DefaultDataHeader,
  },
  props: {
    filter: {
      type: Object,
      default: () => ({
        companyFilter: {
          companies: [],
          regional: [],
          installations: [],
          assets: [],
        },
        generalFilter: {
          selectedAlarmStatus: [],
          selectedAlarmClassifications: [],
          selectedInstallationStatus: [],
          period: -1,
        },
      }),
    },
    completeFilter: {
      type: Object,
      default: () => ({
        companyFilter: {
          companies: [],
          regional: [],
          installations: [],
          assets: [],
        },
        generalFilter: {
          selectedAlarmStatus: [],
          selectedAlarmClassifications: [],
          selectedInstallationStatus: [],
          period: -1,
        },
      }),
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    items() {
      return [
        {
          title: this.$t('header.company'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.companyFilter.companies,
            this.completeFilter.companyFilter.companies,
          ),
        },
        {
          title: this.$t('header.regional'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.companyFilter.regional,
            this.completeFilter.companyFilter.regional,
          ),
        },
        {
          title: this.$t('header.installation'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.companyFilter.installations,
            this.completeFilter.companyFilter.installations,
          ),
        },
        {
          title: this.$t('header.asset'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.companyFilter.assets,
            this.completeFilter.companyFilter.assets,
          ),
        },
        {
          title: this.$t('header.alarmClassification'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.generalFilter.selectedAlarmClassifications,
            this.completeFilter.generalFilter.selectedAlarmClassifications,
          ),
        },
        {
          title: this.$t('header.installationStatus'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.generalFilter.selectedInstallationStatus,
            this.completeFilter.generalFilter.selectedInstallationStatus,
          ),
        },
        {
          title: this.$t('header.alarmStatus'),
          data: this.getItemsIfIsNotAllSelected(
            this.filter.generalFilter.selectedAlarmStatus,
            this.completeFilter.generalFilter.selectedAlarmStatus,
          ),
        },
        {
          title: this.$t('header.period'),
          data: this.getPeriodSelected(this.filter.generalFilter.period),
        },
      ].filter(item => item.data.length);
    },
  },
  methods: {
    getItemsIfIsNotAllSelected(selectedList, completeList) {
      if (!selectedList?.length) {
        return [];
      }
      const selectedListIds = selectedList.map(({ id }) => id);
      const completeListIds = (completeList || []).map(({ id }) => id);

      if (completeListIds.every(item => selectedListIds.includes(item))) {
        return [];
      }

      return selectedList.map(({ name }) => ({ name }));
    },
    getPeriodSelected(period) {
      if (period === periods.ALL) {
        return [];
      }

      return [{ name: this.$t('header.period-days', { days: period }) }];
    },
    onInterval(eventData) {
      this.$emit('on-interval', eventData);
    },
    onSearchHandler(eventData) {
      this.$emit('search', eventData);
    },
    hideFilters() {
      this.$refs.sidebar.showSidebar(false);
    },
    onShowHandler() {
      this.$refs.filterRef.initializeFilter();
    },
    isSidebarVisible() {
      return this.$refs.sidebar?.$data.show ?? false;
    },
    onSearchButtonClickHandler() {
      this.$refs.filterRef.onSearchButtonClick();
    },
  },
};
</script>

<style scoped lang="sass">
@import "src/styles/defaults/mobile-first"
.alarms-header
  &__wrapper
    vertical-align: center
    ::v-deep .material-icons-round
      vertical-align: bottom
    ::v-deep .default-data-header__item
      padding-bottom: 0.2rem
  &__content
    display: flex
    justify-content: flex-end
    align-items: center
    flex-wrap: wrap
    padding: 25px 40px
    height: 100%
    width: 100%
  &__title
    flex-grow: 10
    order: 0
    text-transform: uppercase
    +media-max-md()
      width: 80%
  &__countdown
    display: inline-block
    order: 1
    +media-min-md()
      padding-right: 1.5rem
    +media-max-md()
      width: 100%
      order: 2
  &__sidebar
    flex-grow: 0
    vertical-align: bottom
    display: inline-block
    order: 2
    +media-max-md()
      order: 1
  &__badges
    padding: 1rem 2.7rem 0 2.7rem
</style>
